/**
 * SearchBar Component
 *
 * Functional search bar for finding people in the family tree.
 * Features:
 * - Case-insensitive search
 * - Partial matching
 * - Searches first name, last name, and nickname
 * - Support for Arabic and English text
 * - Dropdown results list
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonNodeCanvas } from '@/types/tree-dashboard.types';

interface SearchBarProps {
  persons: PersonNodeCanvas[];
  onSelectPerson: (personId: string) => void;
  className?: string;
}

interface SearchResult {
  id: string;
  first_name: string;
  last_name: string | null;
  nickname: string | null;
  date_of_birth: string | null;
  date_of_death: string | null;
}

/**
 * Normalize text for search matching (case-insensitive, trim whitespace)
 */
function normalizeSearchText(text: string): string {
  return text.toLowerCase().trim();
}

/**
 * Check if a person matches the search query
 * Searches: first name, last name, nickname (case-insensitive, partial match)
 */
function personMatchesQuery(person: PersonNodeCanvas, normalizedQuery: string): boolean {
  if (!normalizedQuery) return false;

  const firstName = normalizeSearchText(person.first_name || '');
  const lastName = normalizeSearchText(person.last_name || '');
  const nickname = normalizeSearchText(person.nickname || '');
  const fullName = normalizeSearchText(`${person.first_name || ''} ${person.last_name || ''}`.trim());

  // Match against first name, last name, nickname, or full name
  return (
    firstName.includes(normalizedQuery) ||
    lastName.includes(normalizedQuery) ||
    nickname.includes(normalizedQuery) ||
    fullName.includes(normalizedQuery)
  );
}

/**
 * Format person name for display with optional dates
 */
function formatPersonLabel(person: SearchResult): string {
  const name = `${person.first_name} ${person.last_name || ''}`.trim();
  const nickname = person.nickname ? ` (${person.nickname})` : '';

  // Format birth/death years if available
  const birthYear = person.date_of_birth ? new Date(person.date_of_birth).getFullYear() : null;
  const deathYear = person.date_of_death ? new Date(person.date_of_death).getFullYear() : null;

  let dates = '';
  if (birthYear || deathYear) {
    dates = ` • ${birthYear || '?'}–${deathYear || ''}`;
  }

  return `${name}${nickname}${dates}`;
}

export function SearchBar({ persons, onSelectPerson, className = '' }: SearchBarProps) {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter persons based on search query
  const searchResults = useMemo((): SearchResult[] => {
    if (!query.trim()) return [];

    const normalizedQuery = normalizeSearchText(query);
    return persons
      .filter(person => personMatchesQuery(person, normalizedQuery))
      .map(person => ({
        id: person.id,
        first_name: person.first_name,
        last_name: person.last_name,
        nickname: person.nickname,
        date_of_birth: person.date_of_birth,
        date_of_death: person.date_of_death,
      }))
      .slice(0, 10); // Limit to 10 results for performance
  }, [query, persons]);

  // Handle selecting a person from results
  const handleSelectPerson = (personId: string) => {
    onSelectPerson(personId);
    setQuery(''); // Clear search after selection
    setIsOpen(false);
    inputRef.current?.blur(); // Remove focus from input
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.trim().length > 0); // Open dropdown if query is not empty
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (query.trim().length > 0) {
      setIsOpen(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl pointer-events-none">
          search
        </span>
        <input
          ref={inputRef}
          className="w-full lg:w-64 xl:w-72 bg-slate-100 dark:bg-slate-800 border-none rounded-full py-2 pl-10 pr-4 text-sm focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-slate-900 transition-all placeholder:text-slate-400"
          placeholder={t('dashboard.search.placeholder')}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          aria-label={t('dashboard.search.placeholder')}
          autoComplete="off"
        />
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl max-h-96 overflow-y-auto z-[150]"
        >
          {searchResults.length > 0 ? (
            <div className="p-2">
              {/* Results count header */}
              <div className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                {t('dashboard.search.resultsCount', { count: searchResults.length })}
              </div>

              {/* Results list */}
              <div className="mt-1 space-y-0.5">
                {searchResults.map(person => (
                  <button
                    key={person.id}
                    onClick={() => handleSelectPerson(person.id)}
                    className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-primary/10 hover:text-primary dark:hover:text-primary rounded-lg transition-colors"
                  >
                    {formatPersonLabel(person)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-6 text-center">
              <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-700 mb-2 block">
                search_off
              </span>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('dashboard.search.noResults', { query })}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
